/**
 * THE APP'S TWO NAVIGATION SEAMS, BOUND TO THE APP'S ROUTER.
 *
 * `features/support/navigation.ts` was written before the router was installed, as one injected
 * function with a real default - `window.location.assign`, a full page load - and the plan that
 * the router, once it arrived, would be handed to that seam so every call site was already right.
 * The router arrived and nothing handed it over. Every move the support and terms screens made - to
 * `/terms`, to `/sign-in`, to the Payments page - reloaded the whole app, and in a live build that
 * is a second Firebase boot and a second `GET /v1/me` for one click inside the cabinet.
 *
 * THE SECOND SEAM, `./in-app-navigation.ts`, is the same move for an address a screen builds itself
 * - a new draft's editor, the terms interstitial with the way back in its query - and it had the
 * same default, so "Start a new draft" reloaded the app on its way into the editor (2026-09-23).
 * One `push` serves both: every `CabinetRoute` the support seam takes is also an address.
 *
 * Mounted once by the root route in `src/router.tsx`, so every route - inside the shell or not -
 * moves in place. The default stays the default for anything rendered without this router, which
 * is what the support screens' own previews and specs do.
 */
import { useCallback } from 'react';
import type { ReactNode } from 'react';
import { useRouter } from '@tanstack/react-router';
import { NavigateContext } from '../support/navigation';
import { NavigateToHrefContext } from './in-app-navigation';
import type { NavigateToHref } from './in-app-navigation';

export function RouterNavigateProvider({ children }: { readonly children: ReactNode }) {
  const router = useRouter();
  const push = useCallback<NavigateToHref>(
    (href, options) => {
      if (options?.replace === true) {
        router.history.replace(href);
      } else {
        router.history.push(href);
      }
    },
    [router],
  );
  return (
    <NavigateContext.Provider value={push}>
      <NavigateToHrefContext.Provider value={push}>{children}</NavigateToHrefContext.Provider>
    </NavigateContext.Provider>
  );
}
